import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Layout from '@/components/Layout';
import { toast } from 'sonner';
import { X, Save, RotateCcw, Building2, WifiOff } from 'lucide-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import offlineDB from '@/utils/offlineDB';
import { useOnlineStatus } from '@/hooks/usePWA';
import DeliveryInfoDialog from '@/components/DeliveryInfoDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SignaturePad({ token, user, onLogout }) {
  const { noteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [deliveryNote, setDeliveryNote] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [signatureDate, setSignatureDate] = useState(new Date().toISOString().split('T')[0]);
  const [isLoading, setIsLoading] = useState(false);
  const isOnline = useOnlineStatus();
  const [showDeliveryDialog, setShowDeliveryDialog] = useState(false);
  const [signaturePayload, setSignaturePayload] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [showSignatureStep, setShowSignatureStep] = useState(false);

  // Check if user came from tours page
  const fromTours = location.state?.from === 'tours';
  
  const navigateBack = () => {
    if (fromTours) {
      navigate('/tours');
    } else {
      navigate('/delivery-notes');
    }
  };

  useEffect(() => {
    fetchDeliveryNote();
    // Zeige zuerst den Delivery-Dialog
    setShowDeliveryDialog(true);
  }, [noteId]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size for iPad optimization - 100% LARGER
    const container = canvas.parentElement;
    canvas.width = container.clientWidth;
    canvas.height = 600; // Doubled from 300 to 600

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 3; // Increased from 2 to 3
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const fetchDeliveryNote = async () => {
    setIsLoading(true);
    try {
      // Try to fetch from server first
      if (isOnline) {
        const response = await axios.get(`${API}/delivery-notes/${noteId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDeliveryNote(response.data);

        // Fetch customer (if not locked)
        if (response.data.customer_id) {
          const customerRes = await axios.get(`${API}/customers/${response.data.customer_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCustomer(customerRes.data);
        } else {
          // For locked notes, use detected customer name or placeholder
          setCustomer({ 
            company_name: response.data.detected_customer_name || 'Kunde unbekannt' 
          });
        }
      } else {
        // Offline mode - fetch from IndexedDB
        const offlineNote = await offlineDB.getOfflineNote(noteId);
        if (!offlineNote) {
          toast.error('Lieferschein ist offline nicht verfügbar');
          navigateBack();
          return;
        }
        setDeliveryNote(offlineNote);

        // Fetch customer from offline DB
        const offlineCustomers = await offlineDB.getOfflineCustomers();
        const offlineCustomer = offlineCustomers.find(c => c.id === offlineNote.customer_id);
        if (offlineCustomer) {
          setCustomer(offlineCustomer);
        } else {
          setCustomer({ company_name: 'Offline-Kunde', id: offlineNote.customer_id });
        }
      }
    } catch (error) {
      toast.error('Fehler beim Laden des Lieferscheins');
      navigateBack();
    } finally {
      setIsLoading(false);
    }
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Calculate scaled coordinates
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Calculate scaled coordinates
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleDeliveryInfoSubmit = async (deliveryInfoData) => {
    // Delivery Info erhalten - jetzt Unterschrift zeigen
    setDeliveryInfo(deliveryInfoData);
    setShowDeliveryDialog(false);
    setShowSignatureStep(true);
  };

  // Handler für Paketdienst (keine Unterschrift nötig)
  const handleSubmitWithoutSignature = async (deliveryInfoData) => {
    setIsLoading(true);
    try {
      if (isOnline) {
        // Online: Lieferschein als "versendet" markieren ohne Unterschrift
        await axios.post(
          `${API}/delivery-notes/${noteId}/ship`,
          deliveryInfoData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Lieferschein als versendet markiert. E-Mail wird versendet.');
      } else {
        // Offline: Speichern für spätere Sync
        const pendingShipment = {
          id: `shipment_${Date.now()}_${noteId}`,
          type: 'delivery_note_ship',
          body: JSON.stringify({
            delivery_note_id: noteId,
            delivery_info: deliveryInfoData
          }),
          timestamp: new Date().toISOString()
        };
        await offlineDB.addPendingSignature(pendingShipment);
        toast.success('Versandinfo offline gespeichert. Wird automatisch synchronisiert.', {
          duration: 5000,
          icon: <WifiOff className="h-5 w-5" />
        });
      }
      setShowDeliveryDialog(false);
      navigateBack();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setIsLoading(false);
    }
  };

  // Unterschrift mit Delivery Info zusammen speichern
  const handleFinalSubmit = async (e) => {
    e.preventDefault();

    if (!signerName.trim()) {
      toast.error('Bitte geben Sie Ihren Namen ein');
      return;
    }

    // Check if signature is empty
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const isEmpty = !imageData.data.some(channel => channel !== 0);

    if (isEmpty) {
      toast.error('Bitte unterschreiben Sie zuerst');
      return;
    }

    setIsLoading(true);

    try {
      // Get signature as base64
      const signatureData = canvas.toDataURL('image/png');

      const payload = {
        delivery_note_id: noteId,
        signature_data: signatureData,
        signer_name: signerName,
        signature_date: signatureDate,
        delivery_info: deliveryInfo  // Include delivery info in signature payload
      };

      if (isOnline) {
        // Online mode - submit signature with delivery info
        await axios.post(
          `${API}/delivery-notes/sign`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        toast.success('Lieferschein unterschrieben! E-Mail wird versendet.');
        navigateBack();
      } else {
        // Offline mode - save both to IndexedDB
        const pendingSignature = {
          id: `signature_${Date.now()}_${noteId}`,
          type: 'delivery_note',
          body: JSON.stringify(payload),
          timestamp: new Date().toISOString()
        };

        await offlineDB.addPendingSignature(pendingSignature);

        toast.success('Unterschrift offline gespeichert. Wird automatisch synchronisiert.', {
          duration: 5000,
          icon: <WifiOff className="h-5 w-5" />
        });
        navigateBack();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Unterschreiben');
    } finally {
      setIsLoading(false);
    }
  };

  if (!deliveryNote || !customer) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Lade Lieferschein...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="signature-pad">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#0A1F33' }}>Lieferschein unterschreiben</h1>
            <p className="text-gray-600 mt-1">Für {customer.company_name}</p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigateBack()}
            data-testid="back-btn"
          >
            <X className="mr-2 h-4 w-4" />
            Abbrechen
          </Button>
        </div>

        <Card className="glass-effect border-0">
          <CardHeader>
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Kundeninformationen</CardTitle>
                <CardDescription>Lieferschein: {deliveryNote.filename}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-gray-600">Firma:</span>
                <p className="font-medium">{customer.company_name}</p>
              </div>
              {customer.street && (
                <div>
                  <span className="text-gray-600">Adresse:</span>
                  <p className="font-medium">{customer.street}</p>
                  <p className="font-medium">{customer.zipcode} {customer.city}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="glass-effect border-0">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>PDF-Vorschau</CardTitle>
            <CardDescription>Lieferschein im Original-PDF-Format</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-100 rounded-lg p-4 relative">
              {deliveryNote.locked && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                  <div className="transform -rotate-45">
                    <div className="text-red-600 font-bold opacity-30" style={{
                      fontSize: '80px',
                      textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                      letterSpacing: '10px'
                    }}>
                      GESPERRT
                    </div>
                  </div>
                </div>
              )}
              <object
                data={`data:application/pdf;base64,${deliveryNote.pdf_data}`}
                type="application/pdf"
                className="w-full h-96 rounded border"
                title="PDF Vorschau"
              >
                <iframe
                  src={`data:application/pdf;base64,${deliveryNote.pdf_data}`}
                  className="w-full h-96 rounded border"
                  title="PDF Vorschau"
                  data-testid="pdf-preview"
                />
                <p className="text-center text-gray-600 mt-4">
                  PDF kann nicht angezeigt werden. 
                  <a 
                    href={`data:application/pdf;base64,${deliveryNote.pdf_data}`}
                    download={deliveryNote.filename}
                    className="text-blue-600 hover:underline ml-2"
                  >
                    PDF herunterladen
                  </a>
                </p>
              </object>
            </div>
          </CardContent>
        </Card>

        {deliveryNote.locked && (
          <Card className="glass-effect border-0 bg-amber-50">
            <CardHeader>
              <CardTitle className="text-amber-700">⚠️ Lieferschein gesperrt</CardTitle>
              <CardDescription className="text-amber-600">
                Dieser Lieferschein ist keinem Kunden zugeordnet. Unterschreiben nicht möglich.
                {deliveryNote.detected_customer_name && (
                  <span className="block mt-2">
                    Erkannter Kunde: <strong>{deliveryNote.detected_customer_name}</strong>
                  </span>
                )}
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {!deliveryNote.locked && showSignatureStep && (
          <form onSubmit={handleFinalSubmit} className="space-y-6">
            <Card className="glass-effect border-0">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Unterschrift</CardTitle>
                    <CardDescription>Bitte unterschreiben Sie mit dem Finger oder Stift (iPad)</CardDescription>
                  </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={clearSignature}
                  data-testid="clear-signature-btn"
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Löschen
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-white rounded-lg p-2">
                <canvas
                  ref={canvasRef}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  className="w-full cursor-crosshair"
                  style={{ touchAction: 'none' }}
                  data-testid="signature-canvas"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="glass-effect border-0">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Angaben in Druckbuchstaben</CardTitle>
              <CardDescription>Name und Datum für die Unterschrift</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="signer-name">Name (Druckbuchstaben) *</Label>
                <Input
                  id="signer-name"
                  data-testid="signer-name-input"
                  type="text"
                  placeholder="Vor- und Nachname eingeben"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  required
                  className="text-lg"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signature-date">Datum *</Label>
                <Input
                  id="signature-date"
                  data-testid="signature-date-input"
                  type="date"
                  value={signatureDate}
                  onChange={(e) => setSignatureDate(e.target.value)}
                  required
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex space-x-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => navigateBack()}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              className="flex-1 metaxon-primary text-white"
              disabled={isLoading}
              data-testid="submit-signature-btn"
            >
              {isLoading ? (
                'Wird gespeichert...'
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Unterschreiben & Versenden
                </>
              )}
            </Button>
          </div>
        </form>
      )}
      </div>

      {/* Delivery Info Dialog */}
      <DeliveryInfoDialog
        open={showDeliveryDialog}
        onClose={() => {
          setShowDeliveryDialog(false);
          navigateBack();
        }}
        onSubmit={handleDeliveryInfoSubmit}
        onSubmitWithoutSignature={handleSubmitWithoutSignature}
        customerName={customer?.company_name || ''}
        skipPackagingQuestion={customer?.skip_packaging_question || false}
      />
    </Layout>
  );
}
